// FILE: PullRequestSummaryTab.tsx
// Purpose: What a reviewer reads before deciding — description, branches, checks and the size of
//          the change.
// Layer: Component
// Exports: PullRequestSummaryTab
//
// The `+`/`−` counters live here and nowhere else. GitHub's list endpoint returns them as zero
// and only the single-pull-request read carries them, so a row that showed them would need a
// request of its own — which is exactly the cost this section is built to avoid.

import type { PullRequestCheck, PullRequestDetail } from "@taskin-code/contracts";

import ChatMarkdown from "~/components/ChatMarkdown";
import { ArrowRightIcon, GitBranchIcon } from "~/lib/icons";
import { formatRelativeTime } from "~/lib/relativeTime";
import { cn } from "~/lib/utils";
import {
  PullRequestAuthorChip,
  PullRequestLabelDots,
  pullRequestChecksDisplay,
  pullRequestMergeabilityDisplay,
} from "./pullRequestPresentation";

const CHECK_STATUS_CLASS: Record<PullRequestCheck["status"], string> = {
  success: "text-emerald-600 dark:text-emerald-300/90",
  failure: "text-destructive",
  pending: "text-amber-600 dark:text-amber-300/90",
  cancelled: "text-muted-foreground/70",
  neutral: "text-muted-foreground/70",
  skipped: "text-muted-foreground/70",
};

export function PullRequestSummaryTab({ detail }: { detail: PullRequestDetail }) {
  const checks = pullRequestChecksDisplay(detail.checksState);
  const mergeability = pullRequestMergeabilityDisplay(detail.mergeability);

  return (
    <div className="space-y-4 p-3">
      {/* ── Where it lives, and who opened it ─────────────────────────── */}
      <div className="space-y-2 text-[11px] text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium text-muted-foreground/90">{detail.projectTitle}</span>
          <span aria-hidden>·</span>
          <span>{detail.repository}</span>
          <span aria-hidden>·</span>
          <PullRequestAuthorChip author={detail.author} />
          <span aria-hidden>·</span>
          <span>opened {formatRelativeTime(detail.createdAt)} ago</span>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="inline-flex min-w-0 items-center gap-1">
            <GitBranchIcon className="size-3 shrink-0 opacity-70" aria-hidden />
            <span className="truncate font-mono">{detail.headBranch}</span>
            <ArrowRightIcon className="size-3 shrink-0 opacity-50" aria-hidden />
            <span className="truncate font-mono">{detail.baseBranch}</span>
          </span>
          {mergeability ? (
            <span
              className={cn(
                "rounded-sm border px-1.5 py-px text-[10px] font-medium",
                mergeability.className,
              )}
            >
              {mergeability.label}
            </span>
          ) : null}
        </div>

        <PullRequestLabelDots labels={detail.labels} className="flex-wrap" />
      </div>

      {/* ── The size of the change. Only ever known here. ─────────────── */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border/60 bg-background/40 px-3 py-2 text-[11px]">
        <span className="tabular-nums text-emerald-600 dark:text-emerald-300/90">
          +{detail.additions}
        </span>
        <span className="tabular-nums text-destructive">−{detail.deletions}</span>
        <span className="text-muted-foreground">
          {detail.changedFiles} {detail.changedFiles === 1 ? "file" : "files"}
        </span>
        <span className="ms-auto font-mono text-muted-foreground/70">
          {detail.headSha.slice(0, 7)}
        </span>
      </div>

      {/* ── Checks ────────────────────────────────────────────────────── */}
      {checks ? (
        <section className="space-y-1.5">
          <h3 className={cn("flex items-center gap-1.5 text-xs font-medium", checks.colorClass)}>
            <checks.Icon className="size-3.5" aria-hidden />
            {checks.label}
          </h3>
          {detail.checks.length > 0 ? (
            <ul className="space-y-1">
              {detail.checks.map((check) => (
                <li
                  key={`${check.name}:${check.url ?? ""}`}
                  className="flex items-baseline gap-2 text-[11px]"
                >
                  <span className={cn("shrink-0 font-medium", CHECK_STATUS_CLASS[check.status])}>
                    {check.status}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">
                    {check.url ? (
                      <a
                        href={check.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="hover:text-foreground hover:underline"
                      >
                        {check.name}
                      </a>
                    ) : (
                      check.name
                    )}
                    {check.description ? (
                      <span className="text-muted-foreground/70"> — {check.description}</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      {/* ── Reviewers ─────────────────────────────────────────────────── */}
      {detail.reviewers.length > 0 ? (
        <section className="space-y-1.5">
          <h3 className="text-xs font-medium text-foreground">Reviewers</h3>
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
            {detail.reviewers.map((reviewer) => (
              <PullRequestAuthorChip key={reviewer.login} author={reviewer} />
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Description ───────────────────────────────────────────────── */}
      <section className="space-y-1.5">
        <h3 className="text-xs font-medium text-foreground">Description</h3>
        {detail.body.trim().length > 0 ? (
          // `cwd` is undefined on purpose: a pull request body's relative paths belong to a
          // repository on GitHub, not to any workspace on this machine, so they must not resolve
          // into local file links.
          <ChatMarkdown text={detail.body} cwd={undefined} isStreaming={false} />
        ) : (
          <p className="text-[11px] text-muted-foreground/70">
            This pull request was opened with no description.
          </p>
        )}
      </section>
    </div>
  );
}
